import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { AdminFrame } from './AdminFrame';
import { errorText, rpc, SchoolRow } from './api';
import { SchoolPills, SetupProgress } from './pills';

// Schools list (platform layer): type, state, paperwork, setup progress and
// last activity per school, with search and filters. Each card opens the
// school's page; adding a school is its own page.
export function AdminSchools() {
  const { t } = useI18n();
  return (
    <AdminFrame
      section="schools"
      title={t['admin.schools']}
      actions={
        <Link className="btn btn-primary" to="/admin/schools/new">
          {t['admin.addSchool']}
        </Link>
      }
    >
      {() => <SchoolsBody />}
    </AdminFrame>
  );
}

function SchoolsBody() {
  const { t, locale } = useI18n();
  const [schools, setSchools] = useState<SchoolRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [state, setState] = useState('active');

  useEffect(() => {
    rpc<SchoolRow[]>('platform_list_schools').then((r) => {
      if (r.error) setError(errorText(t, r.error));
      else setSchools(r.data);
    });
  }, [t]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (schools ?? []).filter(
      (s) =>
        (!needle || s.name.toLowerCase().includes(needle) || s.code.toLowerCase().includes(needle)) &&
        (!type || s.school_type === type) &&
        (state === 'all' || (state === 'active' ? s.state !== 'offboarded' : s.state === state)),
    );
  }, [schools, q, type, state]);

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : t['pa.never'];

  return (
    <section className="panel p5" aria-labelledby="schools-title">
      <div className="panel-head wrap">
        <h2 className="panel-title" id="schools-title">
          {t['admin.allSchools']}
        </h2>
        <div className="filters">
          <label className="filter">
            <span className="visually-hidden">{t['pa.searchSchools']}</span>
            <input
              className="field"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t['pa.searchSchools']}
            />
          </label>
          <span className="select-wrap">
            <select className="select" aria-label={t['pa.filterType']} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">{t['pa.allTypes']}</option>
              <option value="sandbox">{t['school.sandbox']}</option>
              <option value="customer">{t['school.customer']}</option>
            </select>
          </span>
          <span className="select-wrap">
            <select className="select" aria-label={t['pa.filterState']} value={state} onChange={(e) => setState(e.target.value)}>
              <option value="active">{t['pa.stateNotOffboarded']}</option>
              <option value="setting_up">{t['school.settingUp']}</option>
              <option value="live">{t['school.live']}</option>
              <option value="suspended">{t['school.suspended']}</option>
              <option value="offboarded">{t['school.offboarded']}</option>
              <option value="all">{t['pa.allStates']}</option>
            </select>
          </span>
        </div>
      </div>
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : !schools ? (
        <p role="status" className="text-muted">
          {t.loading}
        </p>
      ) : schools.length === 0 ? (
        <p className="text-muted">
          {t['admin.noSchools']} <Link to="/admin/schools/new">{t['overview.addSchool']}</Link>
        </p>
      ) : shown.length === 0 ? (
        <p className="text-muted">{t['pa.noMatch']}</p>
      ) : (
        <ul className="school-cards" aria-labelledby="schools-title">
          {shown.map((s) => (
            <li key={s.id} className="school-card">
              <div className="school-card-head">
                <h3 className="school-card-name">
                  <Link to={`/admin/schools/${s.id}`}>{s.name}</Link>
                </h3>
                <span className="code-chip">{s.code}</span>
              </div>
              <span className="pill-row">
                <SchoolPills school={s} />
              </span>
              {s.state === 'setting_up' && <SetupProgress done={s.required_done} total={s.required_total} />}
              <dl className="kv kv-inline">
                <div>
                  <dt>{t['pa.municipality']}</dt>
                  <dd>{s.municipality_name ?? '–'}</dd>
                </div>
                <div>
                  <dt>{t['pa.staff']}</dt>
                  <dd>
                    {s.staff_count}
                    {s.test_count > 0 && ` · ${t['pa.nTest'](s.test_count)}`}
                  </dd>
                </div>
                <div>
                  <dt>{t['pa.lastActivity']}</dt>
                  <dd>{fmt(s.last_activity)}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
